import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const IdentityVerification = ({ language = 'fr', onVerificationComplete }) => {
  const [verificationData, setVerificationData] = useState({
    idDocument: null,
    personalPhoto: null,
    documentType: ''
  });
  const [previews, setPreviews] = useState({
    idDocument: null,
    personalPhoto: null
  });
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({});

  const idFileRef = useRef(null);
  const photoFileRef = useRef(null);

  const documentTypes = [
    { 
      value: 'cni', 
      label: language === 'fr' ? 'Carte Nationale d\'Identité' : 'National ID Card',
      icon: 'CreditCard'
    },
    { 
      value: 'passport', 
      label: language === 'fr' ? 'Passeport' : 'Passport',
      icon: 'BookOpen'
    },
    { 
      value: 'driving_license', 
      label: language === 'fr' ? 'Permis de Conduire' : 'Driving License',
      icon: 'Car'
    }
  ];

  const benefits = [
    {
      icon: 'Shield',
      title: language === 'fr' ? 'Compte Vérifié' : 'Verified Account',
      description: language === 'fr' ? 'Badge de confiance visible' : 'Visible trust badge'
    },
    {
      icon: 'TrendingUp',
      title: language === 'fr' ? 'Plus de Ventes' : 'More Sales',
      description: language === 'fr' ? 'Acheteurs préfèrent les vendeurs vérifiés' : 'Buyers prefer verified sellers'
    },
    {
      icon: 'Star',
      title: language === 'fr' ? 'Priorité Support' : 'Priority Support',
      description: language === 'fr' ? 'Support client prioritaire' : 'Priority customer support'
    }
  ];

  const handleFileSelect = (type, file) => {
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        [type]: language === 'fr' ? 'Seules les images sont acceptées' : 'Only images are accepted'
      }));
      return;
    }

    // Validate file size (5MB max)
    if (file?.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        [type]: language === 'fr' ? 'Taille maximale: 5MB' : 'Maximum size: 5MB'
      }));
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviews(prev => ({
        ...prev,
        [type]: e?.target?.result
      }));
    };
    reader?.readAsDataURL(file);

    setVerificationData(prev => ({
      ...prev,
      [type]: file
    }));

    // Clear error
    if (errors?.[type]) {
      setErrors(prev => ({ ...prev, [type]: '' }));
    }
  };

  const handleCameraCapture = (type) => {
    // Simulate camera capture for mobile devices
    if (navigator.mediaDevices && navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices?.getUserMedia({ video: true })?.then(stream => {
          // In a real implementation, you would show camera interface
          // For demo, we'll just trigger file input
          if (type === 'idDocument') {
            idFileRef?.current?.click();
          } else {
            photoFileRef?.current?.click();
          }
          
          // Stop the stream
          stream?.getTracks()?.forEach(track => track?.stop());
        })?.catch(() => {
          // Fallback to file input
          if (type === 'idDocument') {
            idFileRef?.current?.click();
          } else {
            photoFileRef?.current?.click();
          }
        });
    } else {
      // Fallback to file input
      if (type === 'idDocument') {
        idFileRef?.current?.click();
      } else {
        photoFileRef?.current?.click();
      }
    }
  };

  const handleDocumentTypeSelect = (type) => {
    setVerificationData(prev => ({ ...prev, documentType: type }));
  };

  const handleSubmitVerification = async () => {
    if (!verificationData?.idDocument || !verificationData?.personalPhoto || !verificationData?.documentType) {
      setErrors({
        general: language === 'fr' ?'Veuillez compléter tous les champs requis' :'Please complete all required fields'
      });
      return;
    }

    setIsUploading(true);
    
    try {
      // Simulate upload process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (onVerificationComplete) {
        onVerificationComplete({
          documentType: verificationData?.documentType,
          status: 'pending',
          submittedAt: new Date()?.toISOString()
        });
      }
    } catch (error) {
      setErrors({
        general: language === 'fr' ?'Erreur lors de l\'envoi. Veuillez réessayer.' :'Upload error. Please try again.'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (type) => {
    setVerificationData(prev => ({ ...prev, [type]: null }));
    setPreviews(prev => ({ ...prev, [type]: null }));
    if (errors?.[type]) {
      setErrors(prev => ({ ...prev, [type]: '' }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Shield" size={32} className="text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {language === 'fr' ? 'Vérification d\'Identité' : 'Identity Verification'}
        </h2>
        <p className="text-muted-foreground">
          {language === 'fr' ?'Optionnel - Augmentez votre crédibilité sur la plateforme' :'Optional - Increase your credibility on the platform'
          }
        </p>
      </div>
      {/* Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {benefits?.map((benefit, index) => (
          <div key={index} className="bg-success/5 border border-success/20 rounded-lg p-4 text-center">
            <Icon name={benefit?.icon} size={24} className="text-success mx-auto mb-2" />
            <h3 className="font-medium text-foreground mb-1">{benefit?.title}</h3>
            <p className="text-sm text-muted-foreground">{benefit?.description}</p>
          </div>
        ))}
      </div>
      {/* Document Type Selection */}
      <div className="space-y-3">
        <h3 className="font-medium text-foreground">
          {language === 'fr' ? 'Type de document' : 'Document Type'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {documentTypes?.map((doc) => (
            <button
              key={doc?.value}
              onClick={() => handleDocumentTypeSelect(doc?.value)}
              className={`flex items-center space-x-3 p-3 rounded-lg border transition-smooth ${
                verificationData?.documentType === doc?.value
                  ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 text-foreground'
              }`}
            >
              <Icon name={doc?.icon} size={20} />
              <span className="text-sm font-medium">{doc?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* ID Document Upload */}
      <div className="space-y-3">
        <h3 className="font-medium text-foreground">
          {language === 'fr' ? 'Photo du document d\'identité' : 'ID Document Photo'}
        </h3>
        
        {!previews?.idDocument ? (
          <div className="border-2 border-dashed border-border rounded-lg p-6">
            <div className="text-center">
              <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                {language === 'fr' ?'Téléchargez une photo claire de votre document' :'Upload a clear photo of your document'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button
                  variant="outline"
                  onClick={() => idFileRef?.current?.click()}
                  iconName="Upload"
                  iconPosition="left"
                  iconSize={16}
                >
                  {language === 'fr' ? 'Choisir fichier' : 'Choose File'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleCameraCapture('idDocument')}
                  iconName="Camera"
                  iconPosition="left"
                  iconSize={16}
                  className="md:hidden"
                >
                  {language === 'fr' ? 'Prendre photo' : 'Take Photo'}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative">
            <Image
              src={previews?.idDocument}
              alt="ID document preview showing uploaded identification document"
              className="w-full h-48 object-cover rounded-lg border border-border"
            />
            <button
              onClick={() => removeFile('idDocument')}
              className="absolute top-2 right-2 w-8 h-8 bg-error text-error-foreground rounded-full flex items-center justify-center hover:bg-error/80 transition-smooth"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}
        
        {errors?.idDocument && (
          <p className="text-sm text-error">{errors?.idDocument}</p>
        )}
        
        <input
          ref={idFileRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileSelect('idDocument', e?.target?.files?.[0])}
          className="hidden"
        />
      </div>
      {/* Personal Photo Upload */}
      <div className="space-y-3">
        <h3 className="font-medium text-foreground">
          {language === 'fr' ? 'Photo personnelle' : 'Personal Photo'}
        </h3>
        
        {!previews?.personalPhoto ? (
          <div className="border-2 border-dashed border-border rounded-lg p-6">
            <div className="text-center">
              <Icon name="User" size={32} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                {language === 'fr' ?'Téléchargez une photo de vous tenant le document' :'Upload a photo of you holding the document'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button
                  variant="outline"
                  onClick={() => photoFileRef?.current?.click()}
                  iconName="Upload"
                  iconPosition="left"
                  iconSize={16}
                >
                  {language === 'fr' ? 'Choisir fichier' : 'Choose File'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleCameraCapture('personalPhoto')}
                  iconName="Camera"
                  iconPosition="left"
                  iconSize={16}
                  className="md:hidden"
                >
                  {language === 'fr' ? 'Prendre photo' : 'Take Photo'}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative">
            <Image
              src={previews?.personalPhoto}
              alt="Personal photo preview showing user holding identification document"
              className="w-full h-48 object-cover rounded-lg border border-border"
            />
            <button
              onClick={() => removeFile('personalPhoto')}
              className="absolute top-2 right-2 w-8 h-8 bg-error text-error-foreground rounded-full flex items-center justify-center hover:bg-error/80 transition-smooth"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}
        
        {errors?.personalPhoto && (
          <p className="text-sm text-error">{errors?.personalPhoto}</p>
        )}
        
        <input
          ref={photoFileRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileSelect('personalPhoto', e?.target?.files?.[0])}
          className="hidden"
        />
      </div>
      {/* Guidelines */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-accent mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-accent mb-2">
              {language === 'fr' ? 'Conseils pour de meilleures photos:' : 'Tips for better photos:'}
            </p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• {language === 'fr' ? 'Éclairage naturel ou lumineux' : 'Natural or bright lighting'}</li>
              <li>• {language === 'fr' ? 'Document entièrement visible' : 'Document fully visible'}</li>
              <li>• {language === 'fr' ? 'Pas de reflets ou d\'ombres' : 'No glare or shadows'}</li>
              <li>• {language === 'fr' ? 'Photo nette et de bonne qualité' : 'Sharp and good quality photo'}</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Error Message */}
      {errors?.general && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-error" />
            <p className="text-sm text-error">{errors?.general}</p>
          </div>
        </div>
      )}
      {/* Submit Button */}
      <Button
        variant="default"
        fullWidth
        onClick={handleSubmitVerification}
        loading={isUploading}
        disabled={!verificationData?.idDocument || !verificationData?.personalPhoto || !verificationData?.documentType}
        iconName="Shield"
        iconPosition="left"
        iconSize={16}
      >
        {language === 'fr' ? 'Soumettre pour vérification' : 'Submit for Verification'}
      </Button>
      {/* Skip Option */}
      <div className="text-center">
        <button
          onClick={() => onVerificationComplete && onVerificationComplete(null)}
          className="text-sm text-muted-foreground hover:text-foreground transition-smooth"
        >
          {language === 'fr' ? 'Passer cette étape' : 'Skip this step'}
        </button>
      </div>
    </div>
  );
};

export default IdentityVerification;